import React from 'react';
import { RapportPage } from './RapportPage';
import { MM_TO_PIXEL_SCALE } from './RapportMallUtils';
import { RapportEgenskaper } from './RapportEgenskaper';
import {
  IRapportMallControl,
  isRapportAssociationControl,
  isRapportContentControl,
  IRapportContentControl,
  IRapportAssociationControl,
  RAPPORTMALL_TYPES,
} from './RapportMallTypes';
import EDBox from '../EDBox/EDBox';
import { REPORT_SHOW_DOTTED_BORDERS } from '../../Utils/LocalStorage/localStorageKeys';
import { useLocalStorage } from '../../Hooks/useLocalStorage';
import { EDCard } from '../Card/EDCard';

// Typer för rapportmallens JSON-format
export interface IRapportMallJson {
  Title?: string;
  TitleLeft?: number;
  DefaultFont?: string;
  DefaultFontName?: string;
  DefaultFontSize?: string | number;
  PageNoInHeader?: boolean;
  PageNoInFooter?: boolean;
  WatermarkSvk?: boolean;
  Watermark?: string;
  Layout?: boolean;
  LogoInPage?: boolean;
  PageMarginLeft?: number;
  PageMarginTop?: number;
  PageMarginRight?: number;
  PageMarginBottom?: number;
  Body?: IRapportMallControl[];
  Footer?: any;
  FooterSeparator?: boolean;
}

// Type för callback-funktion som uppdaterar en kontroll
export type UpdateControlFunction = (
  controlPath: number[],
  updatedControl: IRapportMallControl
) => void;

interface IEDRapportMallProps {
  /** Titel för rapporten */
  title?: string;
  settings?: { [key: string]: string };
  /** Innehåll som ska visas i rapporten */
  children?: React.ReactNode;
  /** Rapportmall som JSON-sträng */
  mall?: string;
  /** Setter för att uppdatera rapportmallen */
  setMall?: (mall: string) => void;
}

export const A4Width = 210;
export const A4Height = 297;
const DEFAULT_MARGIN_LEFT = 19.0;
const DEFAULT_MARGIN_TOP = 13.0;
const DEFAULT_MARGIN_RIGHT = 11.0;
const DEFAULT_MARGIN_BOTTOM = 5.0;
const DEFAULT_HEADER_HEIGHT = 8.0;

export const parseRapportMallJson = (
  mall: string | undefined
): IRapportMallJson | null => {
  if (!mall) return null;
  try {
    return JSON.parse(mall);
  } catch {
    return null;
  }
};

/**
 * RapportMall - En grundläggande komponent för rapportmallar
 */
export const RapportMall: React.FC<IEDRapportMallProps> = props => {
  const parsedMall: IRapportMallJson | null = parseRapportMallJson(props.mall);

  const pageMarginLeft = parsedMall?.PageMarginLeft ?? DEFAULT_MARGIN_LEFT;
  const pageMarginTop =
    (parsedMall?.PageMarginTop ?? DEFAULT_MARGIN_TOP) + DEFAULT_HEADER_HEIGHT;
  const pageMarginRight = parsedMall?.PageMarginRight ?? DEFAULT_MARGIN_RIGHT;
  const pageMarginBottom =
    parsedMall?.PageMarginBottom ?? DEFAULT_MARGIN_BOTTOM;

  // State för vald kontroll (endast path)
  const [selectedPath, setSelectedPath] = React.useState<number[] | null>(null);

  const [showDottedBorders, setShowDottedBorders] = useLocalStorage<boolean>(
    REPORT_SHOW_DOTTED_BORDERS,
    false
  );

  // Ref för scrollbar container
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Funktion för att scrolla vald kontroll i vy
  const scrollSelectedIntoView = React.useCallback(() => {
    if (!selectedPath || !scrollContainerRef.current) return;

    const pathId = selectedPath.join('-');
    const selectedElement = scrollContainerRef.current.querySelector(
      `[data-control-path="${pathId}"]`
    );

    if (selectedElement) {
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const elementRect = selectedElement.getBoundingClientRect();

      // Kontrollera om elementet är synligt inom container
      const isVisible =
        elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom &&
        elementRect.left >= containerRect.left &&
        elementRect.right <= containerRect.right;

      if (!isVisible) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    }
  }, [selectedPath]);

  // Effekt för att scrolla när selectedPath ändras
  React.useLayoutEffect(
    () => scrollSelectedIntoView(),
    [scrollSelectedIntoView]
  );

  // Funktion för att uppdatera en specifik kontroll i mallen
  const updateControl: UpdateControlFunction = (
    controlPath,
    updatedControl
  ) => {
    if (!parsedMall || !props.setMall) return;

    const updateControlInArray = (
      controls: IRapportMallControl[],
      path: number[],
      pathIndex: number
    ): IRapportMallControl[] => {
      if (pathIndex === path.length - 1) {
        // Vi är på rätt nivå, uppdatera kontrollen
        const newControls = [...controls];
        newControls[path[pathIndex]] = updatedControl;
        return newControls;
      } else {
        // Navigera djupare i strukturen
        const newControls = [...controls];
        const currentControl = newControls[path[pathIndex]];

        if (isRapportContentControl(currentControl)) {
          newControls[path[pathIndex]] = {
            ...currentControl,
            Content: updateControlInArray(
              currentControl.Content || [],
              path,
              pathIndex + 1
            ),
          } as IRapportContentControl;
        } else if (isRapportAssociationControl(currentControl)) {
          newControls[path[pathIndex]] = {
            ...currentControl,
            Template: updateControlInArray(
              currentControl.Template || [],
              path,
              pathIndex + 1
            ),
          } as IRapportAssociationControl;
        }

        return newControls;
      }
    };

    const updatedMall: IRapportMallJson = {
      ...parsedMall,
      Body: updateControlInArray(parsedMall.Body || [], controlPath, 0),
    };

    props.setMall(JSON.stringify(updatedMall, null, 2));
  };

  // Funktion för att lägga till en ny kontroll i mallen
  const addControl = (controlPath: number[] | null, controlType: string) => {
    if (!parsedMall || !props.setMall) return;

    const pathToContainer = (
      controls: IRapportMallControl[],
      path: number[],
      pathIndex: number
    ): number[] => {
      if (pathIndex === path.length) {
        // Vi har nått slutet av sökvägen
        return path;
      }

      const currentIdx = path[pathIndex];
      const currentControl = controls[currentIdx];

      if (isRapportContentControl(currentControl)) {
        return pathToContainer(
          currentControl.Content || [],
          path,
          pathIndex + 1
        );
      } else if (isRapportAssociationControl(currentControl)) {
        return pathToContainer(
          currentControl.Template || [],
          path,
          pathIndex + 1
        );
      } else {
        // Path till förälder
        return path.slice(0, pathIndex);
      }
    };

    const addControlToArray = (
      controls: IRapportMallControl[],
      path: number[],
      pathIndex: number
    ): IRapportMallControl[] => {
      if (pathIndex === path.length) {
        // Lägg till kontrollen på denna nivå
        const initialProps = RAPPORTMALL_TYPES.find(
          c => c.type === controlType
        )?.initialProps;
        const newControl = {
          Type: controlType,
          Height: 5,
          ...initialProps,
        } as IRapportMallControl;
        return [...controls, newControl];
      } else {
        const newControls = [...controls];
        const currentIdx = path[pathIndex];
        const currentControl = newControls[currentIdx];

        if (isRapportContentControl(currentControl)) {
          newControls[currentIdx] = {
            ...currentControl,
            Content: addControlToArray(
              currentControl.Content || [],
              path,
              pathIndex + 1
            ),
          } as IRapportContentControl;
        } else if (isRapportAssociationControl(currentControl)) {
          newControls[currentIdx] = {
            ...currentControl,
            Template: addControlToArray(
              currentControl.Template || [],
              path,
              pathIndex + 1
            ),
          } as IRapportAssociationControl;
        }
        return newControls;
      }
    };

    const pathToLastItem = (
      controls: IRapportMallControl[],
      path: number[],
      pathIndex: number
    ): number[] => {
      if (pathIndex === path.length) {
        // Sista kontrollen på denna nivå
        return [...path, controls.length - 1];
      } else {
        const currentIdx = path[pathIndex];
        const currentControl = controls[currentIdx];

        if (isRapportContentControl(currentControl)) {
          return pathToLastItem(
            currentControl.Content || [],
            path,
            pathIndex + 1
          );
        } else if (isRapportAssociationControl(currentControl)) {
          return pathToLastItem(
            currentControl.Template || [],
            path,
            pathIndex + 1
          );
        } else {
          return path;
        }
      }
    };

    const addToPath = pathToContainer(
      parsedMall?.Body || [],
      controlPath || [],
      0
    );

    const updatedMall: IRapportMallJson = {
      ...parsedMall,
      Body: addControlToArray(parsedMall?.Body || [], addToPath, 0),
    };

    const pathToAdded = pathToLastItem(updatedMall?.Body || [], addToPath, 0);

    props.setMall(JSON.stringify(updatedMall, null, 2));

    // Uppdatera vald path: välj nyligen tillagd kontroll
    setSelectedPath(prev => {
      if (
        (prev ? prev.join('-') : '') !==
        (controlPath ? controlPath.join('-') : '')
      )
        return prev; // om annan path var vald parallellt
      return pathToAdded;
    });
  };

  // Funktion för att ta bort en specifik kontroll i mallen
  const deleteControl = (controlPath: number[]) => {
    if (!parsedMall || !props.setMall) return;

    const deleteControlInArray = (
      controls: IRapportMallControl[],
      path: number[],
      pathIndex: number
    ): IRapportMallControl[] => {
      if (pathIndex === path.length - 1) {
        // Ta bort kontrollen på denna nivå
        return controls.filter((_, idx) => idx !== path[pathIndex]);
      } else {
        const newControls = [...controls];
        const currentIdx = path[pathIndex];
        const currentControl = newControls[currentIdx];

        if (isRapportContentControl(currentControl)) {
          newControls[currentIdx] = {
            ...currentControl,
            Content: deleteControlInArray(
              currentControl.Content || [],
              path,
              pathIndex + 1
            ),
          } as IRapportContentControl;
        } else if (isRapportAssociationControl(currentControl)) {
          newControls[currentIdx] = {
            ...currentControl,
            Template: deleteControlInArray(
              currentControl.Template || [],
              path,
              pathIndex + 1
            ),
          } as IRapportAssociationControl;
        }
        return newControls;
      }
    };

    const updatedMall: IRapportMallJson = {
      ...parsedMall,
      Body: deleteControlInArray(parsedMall.Body || [], controlPath, 0),
    };

    props.setMall(JSON.stringify(updatedMall, null, 2));

    // Uppdatera vald path: välj föregående syskon om möjligt, annars null
    setSelectedPath(prev => {
      if (!prev) return null;
      if (prev.join('-') !== controlPath.join('-')) return prev; // om annan path var vald parallellt
      if (controlPath.length === 0) return null;
      const lastIndex = controlPath[controlPath.length - 1];
      if (lastIndex > 0) {
        return [...controlPath.slice(0, -1), lastIndex - 1];
      }
      return null;
    });
  };

  // Funktion för att dela upp kontroller i sidor baserat på NewPage och returnera även startIndex för varje sida
  const splitControlsToPagesWithStartIndex = (
    controls: IRapportMallControl[] = []
  ) => {
    const pages: { controls: IRapportMallControl[]; startIndex: number }[] = [];
    let currentPage: IRapportMallControl[] = [];
    let currentStart = 0;
    controls.forEach((ctrl, idx) => {
      currentPage.push(ctrl);
      if (ctrl.Type === 'NewPage') {
        pages.push({ controls: currentPage, startIndex: currentStart });
        currentStart += currentPage.length;
        currentPage = [];
      }
    });
    pages.push({ controls: currentPage, startIndex: currentStart });
    return pages;
  };

  return (
    <EDBox sx={{ height: '100%' }}>
      <EDBox
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          mx: 'auto',
          height: { md: '100%' },
          // Responsive: stack on small (md and below), row side-by-side on larger screens
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        {/* Rapportmallsinnehåll */}
        <EDCard flexGrow={1}>
          <EDBox
            ref={scrollContainerRef}
            sx={{ overflow: 'auto', height: { xs: '800px', md: '100%' } }}
          >
            <EDBox
              sx={{
                width: A4Width * MM_TO_PIXEL_SCALE,
                minHeight: A4Height * MM_TO_PIXEL_SCALE,
                pt: 0,
                pl: 2,
                pr: 2,
                background: 'transparent',
                boxShadow: 'none',
                boxSizing: 'border-box',
                m: '8px auto',
                position: 'relative',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                justifyContent: 'center',
              }}
            >
              {splitControlsToPagesWithStartIndex(parsedMall?.Body || []).map(
                ({ controls, startIndex }, pageIdx) => (
                  <RapportPage
                    key={pageIdx}
                    controls={controls}
                    onSelect={setSelectedPath}
                    onUpdate={updateControl}
                    pageIndex={pageIdx}
                    pageTitle={pageIdx === 0 ? parsedMall?.Title : undefined}
                    pageTitleLeft={
                      pageIdx === 0 ? parsedMall?.TitleLeft : undefined
                    }
                    scale={MM_TO_PIXEL_SCALE}
                    startIndex={startIndex}
                    selectedPath={selectedPath || undefined} // Fix: null -> undefined
                    pageMarginLeft={pageMarginLeft}
                    pageMarginTop={pageMarginTop}
                    pageMarginRight={pageMarginRight}
                    pageMarginBottom={pageMarginBottom}
                    showDottedBorders={showDottedBorders}
                  />
                )
              )}
            </EDBox>
          </EDBox>
        </EDCard>
        {/* Egenskapsyta i permanent Drawer till höger */}
        <EDBox
          sx={{
            width: { xs: '100%', md: 300, lg: 400, xl: 600 },
            flexGrow: 0,
            flexShrink: 0,
            height: { xs: '800px', md: '100%' },
          }}
        >
          <EDCard width="100%" flexGrow={0} height="100%">
            <EDBox
              sx={{ width: '100%', boxSizing: 'border-box', height: '100%' }}
            >
              <RapportEgenskaper
                selectedPath={selectedPath}
                onSelectPath={setSelectedPath}
                updateControl={updateControl}
                addControl={addControl}
                deleteControl={deleteControl}
                mall={props.mall}
                setMall={props.setMall}
                parsedMall={parsedMall}
                showDottedBorders={showDottedBorders}
                setShowDottedBorders={setShowDottedBorders}
              />
            </EDBox>
          </EDCard>
        </EDBox>
      </EDBox>
    </EDBox>
  );
};
